import React from 'react';
import type { IconKind } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';

const W = 580;
const H = 470;
const CX = W / 2;
const CY = H / 2;

const CONNECTOR_PATHS = [
  { d: `M ${CX} ${CY - 55} C ${CX} ${CY - 130}, ${CX + 60} ${CY - 165}, ${CX + 90} ${CY - 175}`, delay: 0 },
  { d: `M ${CX - 55} ${CY + 10} C ${CX - 130} ${CY + 20}, ${CX - 200} ${CY + 30}, ${CX - 240} ${CY + 30}`, delay: 0.4 },
  { d: `M ${CX + 50} ${CY - 30} C ${CX + 140} ${CY - 60}, ${CX + 220} ${CY - 110}, ${CX + 250} ${CY - 130}`, delay: 0.8 },
  { d: `M ${CX + 55} ${CY} C ${CX + 150} ${CY + 0}, ${CX + 220} ${CY - 5}, ${CX + 250} ${CY - 10}`, delay: 1.2 },
  { d: `M ${CX + 50} ${CY + 30} C ${CX + 140} ${CY + 60}, ${CX + 220} ${CY + 105}, ${CX + 250} ${CY + 120}`, delay: 1.6 },
  { d: `M ${CX} ${CY + 55} C ${CX} ${CY + 130}, ${CX - 30} ${CY + 165}, ${CX - 30} ${CY + 175}`, delay: 2.0 },
];

const FLOATING_CARDS: Array<{
  icon: IconKind;
  title: string;
  sub: string;
  live?: boolean;
  statusDot?: boolean;
  style: React.CSSProperties;
  delay: string;
}> = [
  {
    icon: 'sparkle',
    title: 'Elva is answering',
    sub: 'AI receptionist',
    live: true,
    style: { top: 22, left: W / 2 - 130 },
    delay: '0.15s',
  },
  {
    icon: 'phone-in',
    title: 'Incoming call',
    sub: '(415) 555-0182 · new patient',
    live: true,
    style: { top: H / 2 - 30, left: 0 },
    delay: '0.30s',
  },
  {
    icon: 'calendar',
    title: 'Booked appointment',
    sub: 'Tue May 20 · 10:00 am',
    style: { top: H / 2 - 165, right: 0 },
    delay: '0.45s',
  },
  {
    icon: 'shield-check',
    title: 'Insurance verified',
    sub: 'Coverage confirmed',
    style: { top: H / 2 - 30, right: 0 },
    delay: '0.60s',
  },
  {
    icon: 'mail',
    title: 'Sent confirmation',
    sub: 'Text & email',
    style: { top: H / 2 + 105, right: 0 },
    delay: '0.75s',
  },
  {
    icon: 'headphones',
    title: 'Listening & responding',
    sub: 'Capturing details',
    statusDot: false,
    style: { bottom: 22, left: W / 2 - 160 },
    delay: '0.90s',
  },
];

function FloatingCard({
  icon,
  title,
  sub,
  live,
  statusDot = true,
  style,
  delay,
}: (typeof FLOATING_CARDS)[number]) {
  return (
    <div
      className="rcpt-empty-card-in absolute z-[4] flex min-w-[190px] max-w-[230px] items-center gap-[11px] rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[13px] py-[11px] shadow-md"
      style={{ ...style, animationDelay: delay }}
    >
      <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color-mix(in_srgb,var(--ai-3)_15%,transparent)] bg-[var(--ai-tint)] text-[var(--ai-3)]">
        <Icon kind={icon} size={14} stroke={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12.5px] font-bold tracking-[-0.01em] text-[var(--ink-0)]">{title}</div>
        <div className="mt-px truncate font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">{sub}</div>
      </div>
      {statusDot ? (
        live ? (
          <span className="elva-live shrink-0" />
        ) : (
          <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[var(--ai-tint)] text-[var(--ai-3)]">
            <Icon kind="check" size={9} stroke={2.5} />
          </span>
        )
      ) : null}
    </div>
  );
}

function CentralOrb() {
  const waveClass = ['rcpt-empty-wave-1', 'rcpt-empty-wave-2', 'rcpt-empty-wave-3', 'rcpt-empty-wave-4', 'rcpt-empty-wave-5', 'rcpt-empty-wave-6'];

  return (
    <div className="absolute left-1/2 top-1/2 z-[3] h-40 w-40 -translate-x-1/2 -translate-y-1/2">
      {[0, 0.85, 1.7, 2.55].map((delay, i) => (
        <span
          key={i}
          className="rcpt-empty-ring"
          style={{ animationDelay: `${delay}s`, opacity: [0.55, 0.45, 0.3, 0.2][i] }}
        />
      ))}

      <div
        className="rcpt-empty-orb-breathe absolute left-1/2 top-1/2 flex h-[120px] w-[120px] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border-[1.5px] border-[color-mix(in_srgb,var(--ai-3)_40%,transparent)] bg-[var(--cream-1)] shadow-[0_0_40px_color-mix(in_srgb,var(--ai-3)_20%,transparent),0_18px_50px_rgba(0,0,0,0.12)]"
      >
        <div
          className="absolute -inset-2 rounded-full opacity-40 blur-[14px]"
          style={{
            background: 'linear-gradient(120deg, var(--ai-3) 0%, var(--ai-4) 50%, var(--ai-5) 100%)',
          }}
        />
        <div className="absolute inset-2.5 rounded-full border border-[var(--cream-2)] bg-[var(--cream-1)]" />
        <div className="relative z-[2] flex h-12 items-center gap-1">
          {waveClass.map((cls, i) => (
            <span
              key={i}
              className={cls}
              style={{
                width: 3,
                height: '100%',
                background: 'var(--ai-3)',
                borderRadius: 999,
                transformOrigin: 'center',
                animationDelay: `${i * 110}ms`,
                boxShadow: '0 0 6px color-mix(in srgb, var(--ai-3) 50%, transparent)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmptyAnimatedIllustration() {
  return (
    <div className="relative h-[470px] w-[580px] shrink-0">
      <div
        className="pointer-events-none absolute -inset-10 z-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, var(--ai-tint) 0%, transparent 60%)',
        }}
      />

      <svg width={W} height={H} className="pointer-events-none absolute left-0 top-0 z-[2]">
        {CONNECTOR_PATHS.map((path, i) => (
          <path
            key={i}
            d={path.d}
            fill="none"
            stroke="var(--ai-3)"
            strokeOpacity="0.45"
            strokeWidth="1.5"
            strokeDasharray="2 6"
            strokeLinecap="round"
            className="rcpt-empty-flow"
            style={{ animationDelay: `${path.delay}s` }}
          />
        ))}
      </svg>

      {FLOATING_CARDS.map((card) => (
        <FloatingCard key={card.title} {...card} />
      ))}

      <CentralOrb />
    </div>
  );
}
